import { t } from '@/features/i18n/utils';
import { Link, Outlet } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { curatorAccessQuery } from '@/features/curator/queries';
import { useAdminOrganizationsAccess } from '@/features/admin-organizations/helpers';
import { adminOrganizationsQuery } from '@/features/admin-organizations/queries';
import { UserRole } from '@/features/user/constants';
import { workspaceTermsQuery } from '@/features/workspace-terms/queries';
import { createFileRoute } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarHeading,
  SidebarSection,
  SidebarSectionPanel,
  SidebarHelpButton,
  SidebarItem,
  SidebarLink,
  SidebarList,
  SidebarReleaseNotes,
  SidebarTrigger,
  SidebarUserMenu,
} from '@/ui/components/sidebar';
import { Main } from '@/ui/components/main';
import { Icon } from '@/ui/components/icon';
import {
  AiBrain02Icon,
  ArrowTurnBackwardIcon,
  Briefcase08Icon,
  Building01Icon,
  Building06Icon,
  CheckmarkBadge01Icon,
  FileBookmarkIcon,
  FolderLibraryIcon,
  InboxIcon,
  NewOfficeIcon,
  Search01Icon,
  SquareLock01Icon,
  SparklesIcon,
  UserGroupIcon,
  UserIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import {
  BottomNavigation,
  BottomNavigationButton,
  BottomNavigationLink,
  BottomNavigationList,
} from '@/ui/components/bottom-navigation';
import { FilePreviewSheet } from '@/features/documents/preview/components/file-preview-sheet';
import { Badge } from '@/ui/components/badge';
import { GlobalSearch } from '@/features/search/components/global-search';
import { ChatAnnouncementCard } from '@/features/chat/components/chat-announcement-card';
import { Button } from '@/ui/components/button';
import { Icons } from '@/ui/components/icons';
import { globalSearchOpenAtom } from '@/features/search/store';
import { useSetAtom } from 'jotai';
import { PROCORE_ENABLED } from '@/features/procore/constants';
import { useQuery } from '@tanstack/react-query';
import { HoverScrollText } from '@/ui/components/hover-scroll-text';

export const Route = createFileRoute('/_authed/settings')({
  component: RouteComponent,
});

function RouteComponent() {
  const auth = useAuth();
  const isAdmin = auth.user.organizationRole === UserRole.ORGANIZATION_ADMIN;
  const { data } = useQuery(curatorAccessQuery());
  const { hasAccess: hasAdminOrganizationsAccess } = useAdminOrganizationsAccess();
  const { data: inactiveOrgCount, isError: organizationsError } = useQuery({
    ...adminOrganizationsQuery(auth.user.id),
    select: (organizations) =>
      organizations.filter((organization) => !organization.isEnabled).length,
    enabled: hasAdminOrganizationsAccess,
  });
  const { data: hasWorkspaceTerms } = useQuery({
    ...workspaceTermsQuery(auth.user.organizationId),
    select: (terms) => terms.length > 0,
  });

  const setSearchOpen = useSetAtom(globalSearchOpenAtom);

  return (
    <div className="flex max-md:flex-col grow min-w-0 bg-background">
      <Sidebar>
        <SidebarContent>
          <SidebarHeader>
            <Button
              nativeButton={false}
              render={<Link to="/" />}
              kind="with-icon"
              variant="ghost"
              className="text-sm! w-full justify-start font-medium"
            >
              <Icon icon={ArrowTurnBackwardIcon} />
              {t({ en: 'Back to app', nb: 'Tilbake til appen' })}
            </Button>
            <GlobalSearch />
            <SidebarTrigger />
          </SidebarHeader>
          <SidebarSection id="personal">
            <SidebarHeading>{t({ en: 'Personal', nb: 'Personlig' })}</SidebarHeading>
            <SidebarSectionPanel>
              <SidebarList>
                <SidebarItem>
                  <SidebarLink to={'/settings/profile'}>
                    <Icon icon={UserIcon} />
                    {t({ en: 'Profile', nb: 'Profil' })}
                  </SidebarLink>
                </SidebarItem>
              </SidebarList>
            </SidebarSectionPanel>
          </SidebarSection>
          <SidebarSection id="administration">
            <SidebarHeading>{t({ en: 'Administration', nb: 'Administrasjon' })}</SidebarHeading>
            <SidebarSectionPanel>
              <SidebarList>
                {auth.user.organizations.length === 1 ? null : (
                  <SidebarItem>
                    <SidebarLink to={'/settings/organizations'}>
                      <Icon icon={Building01Icon} />
                      {t({ en: 'Your organisations', nb: 'Dine organisasjoner' })}
                    </SidebarLink>
                  </SidebarItem>
                )}
                {hasWorkspaceTerms ? (
                  <SidebarItem>
                    <SidebarLink to={'/settings/workspaces'}>
                      <Icon icon={FolderLibraryIcon} />
                      {t({ en: 'Workspaces', nb: 'Arbeidsområder' })}
                    </SidebarLink>
                  </SidebarItem>
                ) : null}
                <SidebarItem>
                  <SidebarLink to={'/settings/members'}>
                    <Icon icon={UserGroupIcon} />
                    {t({ en: 'Team', nb: 'Team' })}
                  </SidebarLink>
                </SidebarItem>
                <SidebarItem>
                  <SidebarLink to={'/settings/knowledge-base'}>
                    <Icon icon={AiBrain02Icon} />
                    {t({ en: 'Knowledge base', nb: 'Kunnskapsbase' })}
                  </SidebarLink>
                </SidebarItem>
                <SidebarItem>
                  <SidebarLink to={'/settings/templates'}>
                    <Icon icon={FileBookmarkIcon} />
                    {t({ en: 'Templates', nb: 'Maler' })}
                  </SidebarLink>
                </SidebarItem>
                <SidebarItem>
                  <SidebarLink to={'/settings/clients'}>
                    <Icon icon={NewOfficeIcon} />
                    {t({ en: 'Clients', nb: 'Klienter' })}
                  </SidebarLink>
                </SidebarItem>
                <SidebarItem>
                  <SidebarLink to={'/settings/suppliers'}>
                    <Icon icon={Briefcase08Icon} />
                    {t({ en: 'Suppliers', nb: 'Leverandører' })}
                  </SidebarLink>
                </SidebarItem>
                {isAdmin && (
                  <SidebarItem>
                    <SidebarLink to={'/settings/sso'}>
                      <Icon icon={SquareLock01Icon} />
                      {t({ en: 'Single sign-on', nb: 'Enkeltpålogging' })}
                    </SidebarLink>
                  </SidebarItem>
                )}
              </SidebarList>
            </SidebarSectionPanel>
          </SidebarSection>
          <SidebarSection id="integrations">
            <SidebarHeading>{t({ en: 'Integrations', nb: 'Integrasjoner' })}</SidebarHeading>
            <SidebarSectionPanel>
              <SidebarList>
                <SidebarItem>
                  <SidebarLink to={'/settings/sharepoint'}>
                    <Icons.sharepoint />
                    {t({ en: 'SharePoint', nb: 'SharePoint' })}
                  </SidebarLink>
                </SidebarItem>
                {PROCORE_ENABLED && (
                  <SidebarItem>
                    <SidebarLink to={'/settings/procore'}>
                      <Icons.procore />
                      {t({ en: 'Procore', nb: 'Procore' })}
                    </SidebarLink>
                  </SidebarItem>
                )}
              </SidebarList>
            </SidebarSectionPanel>
          </SidebarSection>
          {(data?.isCurator || hasAdminOrganizationsAccess) && (
            <SidebarSection id="internal">
              <SidebarHeading>Internal</SidebarHeading>
              <SidebarSectionPanel>
                <SidebarList>
                  {hasAdminOrganizationsAccess && (
                    <SidebarItem>
                      <SidebarLink to={'/settings/internal/organizations'}>
                        <Icon icon={Building06Icon} />
                        <HoverScrollText>
                          {t({ en: 'Organisation approvals', nb: 'Godkjenning av organisasjoner' })}
                        </HoverScrollText>
                        {!organizationsError && (inactiveOrgCount ?? 0) > 0 ? (
                          <Badge
                            size="xs"
                            variant="tertiary"
                            className="ml-auto -mr-1"
                            title={t({ en: 'Inactive', nb: 'Inaktive' })}
                          >
                            {inactiveOrgCount}
                          </Badge>
                        ) : null}
                      </SidebarLink>
                    </SidebarItem>
                  )}
                  {data?.isCurator && (
                    <SidebarItem>
                      <SidebarLink to={'/settings/internal/curator'}>
                        <Icon icon={CheckmarkBadge01Icon} />
                        Curator
                      </SidebarLink>
                    </SidebarItem>
                  )}
                </SidebarList>
              </SidebarSectionPanel>
            </SidebarSection>
          )}
          <div className="mt-auto space-y-3">
            <ChatAnnouncementCard />
            <SidebarList>
              <SidebarHelpButton />
              <SidebarReleaseNotes />
            </SidebarList>
            <SidebarUserMenu />
          </div>
        </SidebarContent>
      </Sidebar>
      <Main>
        <Outlet />
      </Main>
      <FilePreviewSheet />
      <BottomNavigation>
        <BottomNavigationList className="grid-cols-5">
          <BottomNavigationLink to={'/'}>
            <Icon icon={Building06Icon} />
            {t({ en: 'Projects', nb: 'Prosjekter' })}
          </BottomNavigationLink>
          <>
            <BottomNavigationButton onClick={() => setSearchOpen(true)}>
              <Icon icon={Search01Icon} />
              {t({ en: 'Search', nb: 'Søk' })}
            </BottomNavigationButton>
            <BottomNavigationLink to={'/notifications'}>
              <Icon icon={InboxIcon} />
              {t({ en: 'Notifications', nb: 'Notifikasjoner' })}
            </BottomNavigationLink>
            <BottomNavigationLink to={'/learnings'}>
              <Icon icon={SparklesIcon} />
              {t({ en: 'Learnings', nb: 'Erfaringer' })}
            </BottomNavigationLink>
          </>
        </BottomNavigationList>
      </BottomNavigation>
    </div>
  );
}
